import React from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import CardHeader from '@mui/material/CardHeader';
import { Box, IconButton, Popover, Switch, Typography, Button } from '@mui/material';
import Theme from 'theme';
import UploadPatientsDrawer from 'pages/Dashboard/components/UploadPatientsDrawer';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { ToastProps } from 'components/Common/Toast';
import History from 'pages/Dashboard/History';

export interface PatientInfoFormHeaderProps {
  isDemographicForm: boolean;
  setIsDemographicForm: (value: boolean) => void;
  onSuccess?: (value: React.SetStateAction<boolean>) => void;
  setUploadToastProps?: React.Dispatch<React.SetStateAction<ToastProps | null>>
}

export default function PatientInfoFormHeader({
  isDemographicForm,
  setIsDemographicForm,
  onSuccess,
  setUploadToastProps,
}: PatientInfoFormHeaderProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const popoverData = () => (
    <Box sx={{ p: 2, maxWidth: { md: 300 } }}>
      <Typography variant='h6' mb={1}>Settings</Typography>
      <Box display='flex'>
        <Box>
          <Switch
            checked={isDemographicForm}
            onChange={() => {
              setIsDemographicForm(!isDemographicForm);
              closePopover();
            }}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Box>
        <Box>
          <Box display='flex' flexDirection='column'>
            <Typography variant='body2'>Enter demographic info myself</Typography>
            <Typography variant='body2' color='GrayText'>
              Faster medical record access, but requires accurate data entry
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <CardHeader
        action={(
          <Box display='flex' alignItems='center' gap={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <Button
                variant='contained'
                startIcon={<UploadFileIcon />}
                sx={{
                  background: Theme.custom.colors.primaryMain,
                  color: Theme.custom.colors.white,
                  padding: '8px 22px',
                  fontWeight: 500,
                  fontSize: 15,
                  '&:hover': {
                    background: Theme.custom.colors.lightPrimaryMain,
                  },
                }}
                onClick={() => {
                  setDrawerOpen(true);
                  trackMixpanelEvent(MixpanelEventName.UPLOAD_MULTIPLE_PATIENTS_BUTTON_CLICKED, {
                    location: 'Add patients',
                  });
                }}
              >
                UPLOAD MULTIPLE PATIENTS
              </Button>
              <Box ml={1} display='flex' alignItems='center'>
                <Box component='span' display='flex' alignItems='center'>
                  <HistoryIcon sx={{
                    fontSize: 20, color: Theme.custom.colors.primaryMain, mr: 0.5 }}
                  />
                  <Box
                    component='a'
                    sx={{
                      color: Theme.custom.colors.primaryMain,
                      fontWeight: 500,
                      fontSize: 15,
                      cursor: 'pointer',
                      '&:hover': { color: Theme.custom.colors.lightPrimaryMain, textDecoration: 'underline' },
                    }}
                    onClick={() => {
                      trackMixpanelEvent(MixpanelEventName
                        .BULK_UPLOAD_PATIENTS_HISTORY_BUTTON_CLICKED, {
                        location: 'Add patients',
                      });
                      setHistoryDrawerOpen(true);
                    }}
                  >
                    history
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              <IconButton aria-describedby={id} onClick={handleClick}>
                <SettingsIcon />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                {popoverData()}
              </Popover>
            </Box>
          </Box>
        )}
        avatar={isDemographicForm ? <PersonIcon sx={{ color: '#0000008A' }} /> : <PeopleIcon sx={{ color: '#0000008A' }} />}
        title={<Typography variant='h6' color={Theme.custom.colors.lightTextPrimary}>{isDemographicForm ? 'Patient' : 'Patients'}</Typography>}
        sx={{ p: 0, pb: 2 }}
      />
      <UploadPatientsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={onSuccess}
        setToastProps={setUploadToastProps}
      />
      <History
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
      />
    </>
  );
}
